import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import type { AerosNumericFieldProps } from "../AerosNumericField"
import { AerosNumericField } from "../AerosNumericField"

interface FormNumericFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>
  extends Omit<AerosNumericFieldProps, "name" | "defaultValue" | "onChange" | "value"> {
  control: Control<TFieldValues>
  name: TName
  onChange?: AerosNumericFieldProps["onChange"]
  // Opt-in for dense layouts (e.g. a table-like row of many small fields) that surface a single
  // shared error banner elsewhere instead of per-field text — the red border still shows.
  hideErrorMessage?: boolean
}

export const FormNumericField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  helperText,
  onChange,
  hideErrorMessage,
  ...props
}: FormNumericFieldProps<TFieldValues, TName>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange: AerosNumericFieldProps["onChange"] = (value, source) => {
    field.onChange(value)
    onChange?.(value, source)
  }

  return (
    <AerosNumericField
      {...props}
      {...field}
      onChange={handleChange}
      error={!!error}
      helperText={hideErrorMessage ? helperText : (error?.message ?? helperText)}
    />
  )
}
